import { APP_NAME, SUPPORT_EMAIL } from "../app.constants";

export interface LegalSection {
  title: string;
  paragraphs?: readonly string[];
  bullets?: readonly string[];
}

export interface LegalPage {
  slug: string;
  eyebrow: string;
  title: string;
  description: string;
  updated?: string;
  sections: readonly LegalSection[];
  email?: string;
  address?: readonly string[];
}

export const LEGAL_CORE_PAGES: Record<string, LegalPage> = {
  "safety-tips": {
    slug: "safety-tips",
    eyebrow: "SAFETY FIRST",
    title: "Safety tips",
    description: `Practical habits for safer, more comfortable conversations on ${APP_NAME}.`,
    updated: "19 February 2026",
    sections: [
      {
        title: "Your safety comes first",
        paragraphs: [
          `${APP_NAME} helps people connect through one-on-one conversations. Most interactions are positive, but you are communicating with people you may not know. These habits can help you stay comfortable and in control.`,
        ],
      },
      {
        title: "You control the conversation",
        paragraphs: ["You are always in control of how you interact."],
        bullets: [
          "Disconnect at any time",
          "You do not have to answer any question",
          "Skip conversations that do not feel right",
          "Take breaks whenever you want",
        ],
      },
      {
        title: "Protect your personal information",
        paragraphs: [
          "Do not share details that could identify or locate you in the real world, including phone numbers, home or work addresses, financial information, identification details, or personal contact accounts. Once information is shared, it may be difficult to control how it is used.",
        ],
      },
      {
        title: "Be careful with external links",
        paragraphs: ["Not all links shared by other users are safe."],
        bullets: [
          "Avoid unfamiliar links",
          "Do not download files from unknown sources",
          "Be cautious about moving conversations elsewhere",
          "Disconnect if something feels suspicious",
        ],
      },
      {
        title: "Use media thoughtfully",
        paragraphs: [
          "Only share content you are comfortable sharing. Remember that recipients may capture or save what they receive, and never feel pressured to send media. Images are blurred by default so you can decide whether to view them.",
        ],
      },
      {
        title: "Trust your instincts",
        paragraphs: [
          "If something feels unusual, confusing, or uncomfortable, disconnect, use reporting tools, or take a break. You are never obligated to continue interacting.",
        ],
      },
      {
        title: "Be mindful of requests",
        paragraphs: [
          "Consider carefully before responding to requests involving money, personal details, moving to other platforms, or sharing private content. If a request feels inappropriate, decline and move on.",
        ],
      },
      {
        title: "Respect mutual boundaries",
        bullets: [
          "Respect when others decline to answer questions",
          "Do not pressure others for personal information",
          "Accept when someone ends a conversation",
          "Keep interactions friendly and considerate",
        ],
      },
      {
        title: "Use reporting tools when needed",
        paragraphs: [
          "If behavior seems inappropriate or concerning, disconnect, use the report feature, and block the user if available. Reports help us maintain a better experience for everyone.",
        ],
      },
      {
        title: "Stay aware of your environment",
        paragraphs: [
          "For voice or video features, be mindful of what is visible or audible around you. Avoid showing sensitive surroundings and consider your privacy before enabling a camera or microphone.",
        ],
      },
      {
        title: "Take breaks when needed",
        paragraphs: [
          "Online interactions can be tiring. Step away if you feel stressed, limit usage to what feels comfortable, and return when you feel ready.",
        ],
      },
      {
        title: "For parents and guardians",
        paragraphs: [
          `${APP_NAME} is intended for adults. If you believe a younger individual may be using the platform, contact us so we can review the situation.`,
        ],
      },
      {
        title: "Our commitment",
        paragraphs: [
          "We work continuously to maintain a positive environment through moderation tools, safety features, and user feedback. Your awareness and decisions also play an important role in your experience.",
        ],
      },
    ],
    email: SUPPORT_EMAIL,
  },
  privacy: {
    slug: "privacy",
    eyebrow: "YOUR PRIVACY",
    title: "Privacy policy",
    description: `What information ${APP_NAME} collects, why we collect it, and how we use it.`,
    updated: "30 May 2026",
    sections: [
      {
        title: "Who is responsible for your data",
        paragraphs: [
          `${APP_NAME} ("${APP_NAME}", "we", "us") is the data controller responsible for personal information processed through the ${APP_NAME} platform.`,
          `Contact: ${SUPPORT_EMAIL}`,
          "If you are an EU or UK resident with an unresolved data protection complaint, you may contact your national data protection supervisory authority.",
        ],
      },
      {
        title: "What data we collect and why",
        paragraphs: [
          "We collect what is necessary to run, secure, and improve the service.",
        ],
        bullets: [
          "Age confirmation record: a timestamped session record that the 18+ checkbox was accepted, retained for 90 days.",
          "Guest session details: nickname, age range, gender, country, or interests voluntarily entered for matching; deleted when the session ends.",
          "Account information: email, username, and profile details for registered users; retained until account deletion.",
          "Interests: topic categories used to improve matching; removed at session end for guests.",
          "IP address: used for security, abuse prevention, and regional matching; security logs retained for 90 days.",
          "Device and browser information: used to ensure compatibility and distinguish real users from automated bots.",
        ],
      },
      {
        title: "How we use your data",
        bullets: [
          `Operate and maintain ${APP_NAME}`,
          "Match you with other users",
          "Enforce our Terms and Community Guidelines",
          "Detect and prevent abuse, fraud, and illegal activity",
          "Improve the platform based on usage patterns",
          "Comply with legal obligations",
        ],
      },
      {
        title: "Who we share data with",
        paragraphs: [
          "We do not sell your personal data. We share data only with service providers or authorities when necessary: Cloudflare for security and CDN services, Google Analytics for aggregated usage analytics, Google AdSense where applicable with consent, and law enforcement where required by law or to prevent serious harm.",
        ],
      },
      {
        title: "Your rights",
        paragraphs: [
          "Depending on your location, you may have rights to access, correct, delete, restrict, or port your data, object to processing, and withdraw consent where applicable. Contact us to exercise these rights.",
        ],
      },
      {
        title: "Data retention",
        paragraphs: [
          "We retain data only as long as necessary. Guest session data is deleted when the session ends, account data is retained until deletion, and security logs are retained for 90 days.",
        ],
      },
      {
        title: "Cookies",
        paragraphs: [
          "We use cookies and similar technologies. See the Cookie Policy for a full explanation of what we use and why.",
        ],
      },
      {
        title: "Children's privacy",
        paragraphs: [
          `${APP_NAME} is strictly for adults aged 18 and above. If we discover a user is under 18, we will terminate access and delete collected data.`,
        ],
      },
      {
        title: "Changes and contact",
        paragraphs: [
          "We may update this policy as the platform changes. Continued use after an update constitutes acceptance of the current policy.",
        ],
      },
    ],
    email: SUPPORT_EMAIL,
  },
  terms: {
    slug: "terms",
    eyebrow: "THE BASICS",
    title: "Terms of service",
    description: `The rules and responsibilities that keep ${APP_NAME} useful and respectful for everyone.`,
    updated: "30 May 2026",
    sections: [
      {
        title: "Who we are",
        paragraphs: [
          `${APP_NAME} is an anonymous random chat platform connecting people worldwide through one-on-one text conversations. The service is available at strangeryap.fun and associated interfaces.`,
        ],
      },
      {
        title: "What these terms cover",
        paragraphs: [
          `These Terms govern access to the ${APP_NAME} website, chat features, account and guest access, content, tools, and services. By using any part of ${APP_NAME}, including as a guest, you agree to be bound by them.`,
        ],
      },
      {
        title: "Eligibility",
        paragraphs: [
          `${APP_NAME} is strictly for adults. You must be at least 18 years old and have the legal capacity to enter a binding agreement. If local law prohibits use of this type of service, you must not use ${APP_NAME}.`,
        ],
      },
      {
        title: "Guest and registered accounts",
        paragraphs: [
          "Guests receive a temporary session identifier and guest data is cleared when the session ends. Registered users must provide accurate information, keep credentials secure, and accept responsibility for account activity.",
        ],
      },
      {
        title: "Acceptable use",
        paragraphs: [
          `Use ${APP_NAME} lawfully, respectfully, and responsibly.`,
        ],
        bullets: [
          "Never exploit or sexually depict minors or use the service for grooming",
          "Never send unsolicited sexual content or non-consensual intimate imagery",
          "Never threaten, stalk, harass, impersonate, dox, or discriminate against others",
          "Never promote violence, self-harm, terrorism, spam, commercial solicitation, or bots",
          "Never bypass bans or submit false or malicious reports",
        ],
      },
      {
        title: "Content and termination",
        paragraphs: [
          `You retain ownership of content you create. You grant ${APP_NAME} a limited license to process and transmit it as needed to provide the service. We may suspend or terminate access for violations, and you may delete your account at any time.`,
        ],
      },
      {
        title: "Disclaimers and liability",
        paragraphs: [
          `${APP_NAME} is provided as available without warranties of any kind. To the maximum extent permitted by law, ${APP_NAME} is not liable for indirect, incidental, special, consequential, or punitive damages arising from use of the service.`,
        ],
      },
      {
        title: "Governing law and changes",
        paragraphs: [
          "These Terms are governed by the laws of Nova Scotia, Canada. We may update them at any time and will update the date when changes are made.",
        ],
      },
      {
        title: "Contact",
        paragraphs: [
          `For questions about these Terms, contact ${SUPPORT_EMAIL}.`,
        ],
      },
    ],
    email: SUPPORT_EMAIL,
  },
};
